import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { InsurenetInterfaceService } from '@core';
import { parseDateString } from '@co-3/ctrl-ui/common';
import { Contract } from '@models/contract';
import {
    DamageReport,
    DamageReportFileAttachment,
    DamageReportFileAttachmentDto,
    damageReportFileAttachmentFromDto,
    DamageReportValidatorConfig,
} from '@models/damage-report';
import { ProductsService } from 'app/products/products.service';
import {
    BehaviorSubject,
    catchError,
    concatMap,
    EMPTY,
    filter,
    from,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    switchMap,
    take,
    tap,
    toArray,
} from 'rxjs';
import { defaultDamageReportValidators } from '../config/default-damage-report-validators';
import { ContractsService } from '../contracts.service';
import { DamageReportsService } from './damage-reports.service';
import {
    DamageReportForm,
    DamageReportStep,
    DamageReportStepConfig,
    DamageReportSummary,
    FileFormGroup,
    FileWithMeta,
    fileWithMetaToFormData,
    isSameFile,
    UploadStatus,
    WithUploadStatus,
} from './models';

/**
 * @description Use this service to manage the creation of a new
 * damage report. It provides methods to manage file attachments
 * and the form for the damage report properties, as well as uploading
 * files for an already existing report.
 */
@Injectable({ providedIn: 'root' })
export class CreateDamageReportService {
    private damageReportsService = inject(DamageReportsService);
    private contractsService = inject(ContractsService);
    private productsService = inject(ProductsService);
    private insurnetInterfaceService = inject(InsurenetInterfaceService);
    private _selectedContract$: BehaviorSubject<Contract | null> = new BehaviorSubject<Contract | null>(null);

    /**
     * @description returns an observable of the currently
     * selected contract.
     */
    public get selectedContract$(): Observable<Contract | null> {
        return this._selectedContract$.asObservable();
    }

    private readonly form: DamageReportForm = new FormGroup({
        report: new FormGroup({
            damageDate: new FormControl<string>('', Validators.required),
            location: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
            eventDescription: new FormControl<string>('', { nonNullable: true }),
            damageDescription: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
            notes: new FormControl<string>('', { nonNullable: true }),
        }),
        files: new FormArray<FormGroup<FileFormGroup>>([]),
    });

    /**
     * @description returns the report section of the
     * form that can be referenced in the damage
     * report steps.
     */
    public get reportForm(): typeof this.form.controls.report {
        return this.form.controls.report;
    }

    public get filesForm(): typeof this.form.controls.files {
        return this.form.controls.files;
    }

    public get pristine(): boolean {
        return this.form.pristine;
    }

    public get dirty(): boolean {
        return this.form.dirty;
    }

    public get valid(): boolean {
        return this.form.valid;
    }

    public get invalid(): boolean {
        return this.form.invalid;
    }

    /**
     * @description returns an observable of all selected
     * files with metadata to be attached to the damage report.
     */
    public get filesWithMeta$(): Observable<WithUploadStatus<FileWithMeta>[]> {
        return this.form.controls.files.valueChanges.pipe(
            startWith(this.form.controls.files.value),
            map((files) => files as WithUploadStatus<FileWithMeta>[]),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    /**
     * @description returns a summary of the current
     * damage report. The summary contains the report
     * forms values, the selected contract and all
     * files to be attached.
     */
    public get summary(): DamageReportSummary {
        const { damageDate, ...formValues } = this.form.controls.report.getRawValue();
        const parsedDamageDate = parseDateString(damageDate, 'dd.MM.yyyy');
        return {
            ...formValues,
            damageDate: parsedDamageDate,
            contract: this._selectedContract$.getValue(),
            attachments: this.form.controls.files.value as WithUploadStatus<FileWithMeta>[],
        };
    }

    /**
     * @description Selects the contract for which the damage
     * report will be created on submit. Setting a new contract
     * will reset the form and drop any already selected file
     * attachments.
     * @param {Contract|number} contract Contract or contract Id
     * to select.
     */
    public selectContract(contract: Contract | number): void {
        if (typeof contract === 'object') {
            contract = contract.id;
        }
        this.contractsService
            .get(contract)
            .pipe(
                take(1),
                // only reset the service when the contract is not the current one:
                filter((result: Contract) => result?.id !== this._selectedContract$.getValue()?.id),
            )
            .subscribe((result: Contract) => {
                this._selectedContract$.next(result);
                this.resetForm();
                this.updateValidators(result);
            });
    }

    public hasFile(file: File | FileWithMeta): boolean {
        if (!(file instanceof File)) {
            file = file.file;
        }
        return this.form.controls.files.value.some((item: FileWithMeta) => isSameFile(item.file, file));
    }

    /**
     * @description Adds a file with metadata to the list of
     * attached files for the damage report.
     * @param {FileWithMeta | undefined} fileWithMeta the file to add
     */
    public addFile(fileWithMeta?: FileWithMeta): void {
        // don't allow duplicate files:
        if (fileWithMeta && this.hasFile(fileWithMeta)) {
            return;
        }

        this.form.controls.files.push(
            new FormGroup<FileFormGroup>({
                file: new FormControl<File>(fileWithMeta?.file ?? null, Validators.required),
                documentTypeId: new FormControl<number | null>(
                    fileWithMeta?.documentTypeId ?? null,
                    Validators.required,
                ),
                description: new FormControl<string>(fileWithMeta?.description ?? ''),
                status: new FormControl<UploadStatus>(UploadStatus.READY),
            }),
        );
    }

    /**
     * @description Updates an already attached files metadata.
     * @param {Partial<WithUploadStatus<FileWithMeta>> & Pick<FileWithMeta, 'file'>} fileWithMeta the file to update
     */
    public updateFile(fileWithMeta: Partial<WithUploadStatus<FileWithMeta>> & Pick<FileWithMeta, 'file'>): void {
        const index = this.filesForm.value.findIndex(
            (existingFileWithMeta: FileWithMeta) =>
                existingFileWithMeta?.file && isSameFile(existingFileWithMeta.file, fileWithMeta.file),
        );

        if (index !== -1) {
            this.filesForm.at(index).patchValue(fileWithMeta);
        }
    }

    /**
     * @description Remove a file from the list of attached files.
     * @param {Pick<FileWithMeta, 'file'>} fileWithMeta the file to remove.
     */
    public removeFile(fileWithMeta: Pick<FileWithMeta, 'file'>): void {
        const index = this.filesForm.value.findIndex(
            (existingFileWithMeta: FileWithMeta) =>
                existingFileWithMeta?.file && isSameFile(existingFileWithMeta.file, fileWithMeta.file),
        );

        if (index !== -1) {
            this.filesForm.removeAt(index);
        }
    }

    /**
     * @description Submit the current damage report. This will first try
     * to create a new damage report for the selected contract, then
     * proceed to upload the attached files one by one when the contract
     * was successfully created. The files upload status will be updated
     * with the result of the upload attempts. It returns an observable of
     * the created `DamageReport` you can then use for retries of failed
     * file uploads or to attach more files to. See {@link submitFilesOnly}.
     * @returns {Observable<DamageReport>} an observable of the created
     * damage report when successfull.
     */
    public submit(): Observable<DamageReport> {
        const { id: contractId } = this._selectedContract$.getValue();
        if (this.invalid) {
            return;
        }
        if (isNaN(contractId)) {
            throw new Error(
                'CreateDamageReportService.submit: No contract selected. Please select a contract before attempting to create a damage report or upload files',
            );
        }
        const { damageDate, ...formValues } = this.form.controls.report.getRawValue();
        const parsedDamageDate = parseDateString(damageDate, 'dd.MM.yyyy');
        return this.damageReportsService
            .create({ contractId, ...formValues, damageDate: parsedDamageDate, createdAt: new Date() })
            .pipe(
                switchMap((damageReport: DamageReport) =>
                    from([...this.filesForm.value]).pipe(
                        filter(
                            (fileWithMeta: WithUploadStatus<FileWithMeta>) =>
                                fileWithMeta.status === UploadStatus.READY ||
                                fileWithMeta.status === UploadStatus.ERROR,
                        ),
                        concatMap((fileWithMeta: FileWithMeta) => this.uploadFile(damageReport.id, fileWithMeta)),
                        toArray(),
                        map(() => damageReport),
                    ),
                ),
            );
    }

    /**
     * @description Attempts to upload all attached files that
     * have not been uploaded already. Use this to retry failed
     * uploads or attach additional files to an already existing
     * damage report.
     * @param {number} damageReportId the id of the damage report
     * the files should be attached to.
     * @param {WithUploadStatus<FileWithMeta>[]} files an array of files to upload. if this
     * is undefined it will attempt to upload the files from the form instead.
     * @returns {Observable<DamageReportFileAttachment[]>} observable of an array of all
     * uploaded files.
     */
    public submitFilesOnly(
        damageReportId: number,
        files?: WithUploadStatus<FileWithMeta>[],
    ): Observable<DamageReportFileAttachment[]> {
        if (!files?.length && this.filesForm.value.length === 0) {
            return of([]);
        }
        return from(files ?? [...this.filesForm.value]).pipe(
            filter(
                (fileWithMeta: WithUploadStatus<FileWithMeta>) =>
                    fileWithMeta.status === UploadStatus.READY || fileWithMeta.status === UploadStatus.ERROR,
            ),
            concatMap((fileWithMeta: FileWithMeta) => this.uploadFile(damageReportId, fileWithMeta)),
            toArray(),
        );
    }

    /**
     * @description Resets the service. Resets the form, drops all
     * attached files and deselects the selected contract.
     */
    public reset(): void {
        this.resetForm();
        this._selectedContract$.next(null);
    }

    /**
     * @description Resets the form and drops all attached files.
     */
    public resetForm(): void {
        this.form.reset();
        this.filesForm.clear();
    }

    /**
     * @description Uploads a single file for a given damage report. Updates
     * the file's {@link UploadStatus} accordingly.
     * @param {number} damageId the id of the damage report the file should
     * be attached to.
     * @param {FileWithMeta} fileWithMeta File with metadata to upload.
     * @returns {Observable<DamageReportFileAttachment>} An observable of
     * the returned file attachment when successfull.
     */
    private uploadFile(damageId: number, fileWithMeta: FileWithMeta): Observable<DamageReportFileAttachment> {
        const formData = fileWithMetaToFormData(fileWithMeta);

        this.updateFile({ ...fileWithMeta, status: UploadStatus.PENDING });

        return this.insurnetInterfaceService.uploadDamageDocument(damageId, formData).pipe(
            catchError(() => {
                this.updateFile({ ...fileWithMeta, status: UploadStatus.ERROR });
                return EMPTY;
            }),
            tap(() => this.updateFile({ ...fileWithMeta, status: UploadStatus.DONE })),
            map((dto: DamageReportFileAttachmentDto) => damageReportFileAttachmentFromDto(dto)),
        );
    }

    public canActivate(config: DamageReportStepConfig<DamageReportStep>): boolean {
        if (config.guard) {
            return config.guard(this.form);
        }
        return true;
    }

    private updateValidators(contract: Contract): void {
        const product = this.productsService.getCanonicalByInsuranceType(contract.insuranceType);
        let validatorConfig: DamageReportValidatorConfig = { ...defaultDamageReportValidators };

        if (product?.portalConfig?.damageReportValidators) {
            validatorConfig = { ...validatorConfig, ...product.portalConfig.damageReportValidators };
        }

        for (const key in validatorConfig) {
            if (!Object.hasOwn(validatorConfig, key)) {
                continue;
            }
            (this.form.controls.report.controls[key] as FormControl).setValidators(
                typeof validatorConfig[key] === 'function' ? validatorConfig[key](contract) : validatorConfig[key],
            );
        }

        this.form.controls.report.updateValueAndValidity();
    }
}
